/**
 * `ops` procedure group — the LLM spend dashboard (design §1.8, and the plan's
 * "Ops: LLM spend by task/tier + cache-hit alarm").
 *
 * `cacheHitRatio` is the number this endpoint exists for. The whole
 * ~$9-12/user/month model assumes cached prompt tokens stay high, and a cache
 * regression has NO other symptom: quality is unchanged, latency barely moves,
 * nothing throws, and the bill quietly multiplies. Reporting it next to the
 * spend is what makes the regression visible before the invoice does.
 */

import { z } from 'zod';
import { isoDateTimeSchema } from './common.ts';
import { budgetStateSchema } from './enums.ts';

const spendByKeySchema = z.object({
  key: z.string(),
  calls: z.int().min(0),
  costMicroUsd: z.int().min(0),
  promptTokens: z.int().min(0),
  cachedPromptTokens: z.int().min(0),
  completionTokens: z.int().min(0),
});

export const llmSpendInputSchema = z.object({
  /** `YYYY-MM-01`. Defaults to the current month. */
  periodMonth: z
    .string()
    .regex(/^\d{4}-\d{2}-01$/)
    .optional(),
});

export const llmSpendOutputSchema = z.object({
  periodMonth: z.string(),
  totalCostMicroUsd: z.int().min(0),
  calls: z.int().min(0),
  /**
   * Cached prompt tokens / all prompt tokens, in [0,1]. Watch this, not the
   * total: a fall here is a cost regression that nothing else reports.
   */
  cacheHitRatio: z.number().min(0).max(1),
  /**
   * Calls whose model had no `model_prices` row, so their cost is UNKNOWN
   * rather than zero. A non-zero value here means the total understates spend.
   */
  unpricedCalls: z.int().min(0),
  escalatedCalls: z.int().min(0),
  guardFailedCalls: z.int().min(0),
  byTask: z.array(spendByKeySchema),
  byTier: z.array(spendByKeySchema),
  budget: z.object({
    state: budgetStateSchema,
    spendMicroUsd: z.int().min(0),
    softCapMicroUsd: z.int().min(0),
    hardCapMicroUsd: z.int().min(0),
    hardCapOverridden: z.boolean(),
    /** The exact sentence the in-app banner shows. Null when there is none. */
    banner: z.string().nullable(),
    /** Cut from 5 to 3 at the soft cap. */
    digestDraftCount: z.int().min(0),
  }),
  asOf: isoDateTimeSchema,
});

export type LlmSpendOutput = z.output<typeof llmSpendOutputSchema>;
